// Helpers shared by two or more settings screens.
//
// Nothing here is an abstraction over a screen. It is the small set of things
// that were copied into both settings.tsx and settings_admin.tsx before those
// files were split, plus the two or three formatters more than one screen uses.

import { useEffect, useRef, useState } from "react";

import { ApiError, http } from "../api";
import { type Me } from "../App";
import { Badge, Banner } from "../ui";

export const PAGE_SIZE = 20;

export const BASE_PATH = String(
  (window as unknown as { __BASE_PATH__?: string }).__BASE_PATH__ ?? ""
).replace(/\/$/, "");

export function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Error state for a whole screen. The trace id is the only support handle. */
export function ErrorBanner({ error, what }: { error: unknown; what: string }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <Banner tone="critical" traceId={api?.traceId}>
      {what}: {errMessage(error, "request failed")}
    </Banner>
  );
}

/** Search box that only hits the server once typing settles. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return settled;
}

/**
 * useSession().can only types the three core actions, and contacts carry
 * `import` / `export` permissions as well, so those are read off the session
 * directly rather than widening a shared signature.
 */
export function hasAction(me: Me | null, resource: string, action: string): boolean {
  if (!me) return false;
  if (me.is_super_admin) return true;
  return Boolean(me.permissions?.[resource]?.includes(action));
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function fmtDateTime(value: string | null | undefined, blank = "Never"): string {
  if (!value) return blank;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? blank
    : d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

export function titleCase(value: string): string {
  return String(value || "")
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ActiveBadge({ active }: { active: boolean }) {
  return <Badge tone={active ? "good" : "neutral"}>{active ? "Active" : "Inactive"}</Badge>;
}

/**
 * A checkbox that can also sit in the "some but not all" state. `indeterminate`
 * is a DOM property with no HTML attribute, so it has to be written through a
 * ref after every render that changes it.
 */
export function TriCheckbox({
  checked,
  indeterminate,
  disabled,
  ariaLabel,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (next: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate) && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}

/** Key/value editor shared by the webhook and custom-action dialogs. */
export function HeadersEditor({
  headers,
  onChange,
  disabled,
}: {
  headers: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
}) {
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");

  const add = () => {
    if (!key.trim() || !value.trim()) return;
    onChange({ ...headers, [key.trim()]: value.trim() });
    setKey("");
    setValue("");
  };

  const remove = (name: string) => {
    const next = { ...headers };
    delete next[name];
    onChange(next);
  };

  const names = Object.keys(headers);

  return (
    <div className="col">
      {names.length === 0 && <span className="muted">No custom headers.</span>}
      {names.map((name) => (
        <div className="row" key={name}>
          <Badge>{name}</Badge>
          <span className="truncate spacer">{headers[name]}</span>
          <button
            className="btn icon sm ghost"
            title={`Remove header ${name}`}
            aria-label={`Remove header ${name}`}
            disabled={disabled}
            onClick={() => remove(name)}
          >
            ✕
          </button>
        </div>
      ))}
      <div className="row">
        <input
          value={key}
          placeholder="Header name"
          aria-label="Header name"
          disabled={disabled}
          onChange={(e) => setKey(e.target.value)}
        />
        <input
          value={value}
          placeholder="Header value"
          aria-label="Header value"
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
        />
        <button className="btn" disabled={disabled || !key.trim() || !value.trim()} onClick={add}>
          Add
        </button>
      </div>
    </div>
  );
}

export interface AccountLite {
  id: string;
  name: string;
  phone_id?: string;
  phone_number?: string;
}

interface AccountListResponse {
  accounts?: AccountLite[];
}

/** The account picker on Templates, Flows and Contacts. */
export function useAccounts(): AccountLite[] {
  const [accounts, setAccounts] = useState<AccountLite[]>([]);
  useEffect(() => {
    void http
      .get<AccountListResponse>("/accounts")
      .then((res) => setAccounts(res.accounts ?? []))
      // Silent: every screen here still works without the picker.
      .catch(() => setAccounts([]));
  }, []);
  return accounts;
}
